import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import clsx from 'clsx'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { _searchTileContainerQueryUp } from './_MetaSearchTileBase'
import { searchTileContentSpacingStyles } from './searchTileLayoutStyles'

const Container = styled.div<{ $density: SearchTileDensity }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: ${props => props.$density === 'compact' ? rem(208) : 'auto'};
  ${searchTileContentSpacingStyles}

  &.hugContent {
    min-height: unset;
    height: max-content;
    align-self: start;
  }
`

const ContentContainer = styled.div<{ $density: SearchTileDensity }>`
  flex-grow: 1;
  grid-area: content;
  display: grid;
  min-width: 0;
  gap: ${props => props.$density === 'compact' ? rem(12) : rem(16)};
  ${props => props.$density === 'compact' ?
    css`
      grid-template:
        "content-primary" auto
        "content-secondary" auto / 1fr;
      align-items: start;
    ` :
    css`
      grid-template:
        "content-primary" auto
        "content-secondary" min-content / 1fr;
    `}

  & > * {
    min-width: 0;
  }

  &.hugContent {
    flex-grow: 0;
    align-content: start;
  }

  ${_searchTileContainerQueryUp.tablet} {
    ${props => props.$density === 'compact' ?
      css`
        grid-template:
          "content-primary" 1fr
          "content-secondary" auto / 1fr;
        align-items: start;
      ` :
      css`
        grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(432)};
      `}
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    grid-template:
      "content-primary" auto
      "content-secondary" min-content / 1fr;
  }

  ${_searchTileContainerQueryUp.desktopLarge} {
    ${props => props.$density === 'compact' ?
      css`
        grid-template:
          "content-primary" 1fr
          "content-secondary" auto / 1fr;
      ` :
      css`
        grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(432)};
      `}
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  /**
   * Supporting content that always sits at the bottom of the content
   */
  bottomExtension?: ReactNode
  /**
   * Supporting content that always sits at the top of the content
   */
  topExtension?: ReactNode
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function MetaSearchTileContent({ children, bottomExtension, topExtension, density = 'default' }: Props) {
  const hugContent = useMetaSearchMobileV2Enabled()

  return <Container $density={density} className={clsx({ hugContent })}>
    {!!topExtension && <Extension>{topExtension}</Extension>}
    <ContentContainer
      $density={density}
      className={clsx('_MetaSearchTileContent', { hugContent })}
    >
      {children}
    </ContentContainer>
    {!!bottomExtension && <Extension>{bottomExtension}</Extension>}
  </Container>
}

export default MetaSearchTileContent
